import StyledModal from '@/components/StyledModal/StyledModal.tsx';
import StatusIndicator from '@/components/StatusIndicator.tsx';
import useRetrieveRunLlmUsage from '@/hooks/api/llmUsage/useRetrieveRunLlmUsage.ts';
import {
  getRunAutomationVersionLabel,
  getRunSourceLabel,
  getRunVersionStateLabel,
  Run,
  RunWithDatasets,
} from '@/types/runs.ts';
import MetadataJsonViewer from '@/components/MetadataJsonViewer.tsx';
import { useEffect } from 'react';
import useRetrieveRunWithDatasets from '@/hooks/api/runs/useRetrieveRunWithDatasets.ts';
import { Box, Group, Loader, Stack, Text } from '@mantine/core';
import { formatCalendarStyle, formatDuration } from '@/utils/datetime.ts';
import { ActiveStatuses } from '@/types/statuses.ts';

type Props = {
  runWithDatasets: RunWithDatasets;
  opened: boolean;
  onClose: () => void;
};

const RUN_METADATA_SUMMARY_CARD_STYLE = {
  border: '1px solid #ECECED',
  borderRadius: '6px',
};

const parseDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const secondsBetween = (start: Date, end: Date): number | null => {
  const seconds = (end.getTime() - start.getTime()) / 1000;
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
};

const getLatestExecutionRuntime = (run: Run): number | null => {
  const startedAt = parseDate(run.latest_execution?.created_at);
  if (!startedAt || !run.latest_execution) return null;

  if (run.latest_execution.completed != null) {
    const completedAt = parseDate(run.latest_execution.completed);
    return completedAt ? secondsBetween(startedAt, completedAt) : null;
  }

  if (ActiveStatuses.includes(run.status)) return secondsBetween(startedAt, new Date());

  return null;
};

const SummaryItem = ({ label, value }: { label: string; value: string }) => (
  <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
    <Text size="xs" c="dimmed" fw={600}>
      {label}
    </Text>
    <Text size="sm" fw={600} truncate="end">
      {value}
    </Text>
  </Stack>
);

const RunSummaryCard = ({ run }: { run: Run }) => {
  const isActive = ActiveStatuses.includes(run.status);
  const latestExecutionCompletedAt = run.latest_execution?.completed;
  const latestExecutionHasFinished = !!parseDate(latestExecutionCompletedAt);
  const latestExecutionIsRunning =
    !!run.latest_execution && latestExecutionCompletedAt == null && isActive;
  const latestExecutionRuntime = getLatestExecutionRuntime(run);
  const latestExecutionState = latestExecutionHasFinished
    ? 'Finished'
    : latestExecutionIsRunning
      ? 'Running'
      : 'Incomplete';
  const latestExecutionLabel = run.latest_execution
    ? `#${run.latest_execution.index + 1} · ${latestExecutionState}`
    : 'Not started';
  const latestExecutionFinished = latestExecutionIsRunning
    ? 'Running'
    : formatCalendarStyle(latestExecutionCompletedAt);

  return (
    <Box
      px="md"
      py="xs"
      bg="#F8F9FA"
      style={RUN_METADATA_SUMMARY_CARD_STYLE}
    >
      <Group align="flex-start" gap="xs" wrap="nowrap">
        <SummaryItem
          label={isActive ? 'Completed runtime' : 'Total runtime'}
          value={formatDuration(run.total_runtime)}
        />
        <SummaryItem
          label={latestExecutionIsRunning ? 'Exec elapsed' : 'Exec runtime'}
          value={`${formatDuration(latestExecutionRuntime)}${latestExecutionIsRunning && latestExecutionRuntime !== null ? ' elapsed' : ''}`}
        />
        <SummaryItem label="Latest exec" value={latestExecutionLabel} />
        <SummaryItem label="Started" value={formatCalendarStyle(run.latest_execution?.created_at)} />
        <SummaryItem label="Finished" value={latestExecutionFinished} />
      </Group>
    </Box>
  );
};

const RunMetadataModal = ({ runWithDatasets, opened, onClose }: Props) => {
  const {
    data: latestRunWithDatasets,
    isFetching: isFetchingRunWithDatasets,
    refetch: refetchRunWithDatasets,
  } = useRetrieveRunWithDatasets();
  const {
    data: llmUsage,
    isFetching: isFetchingLlmUsage,
    refetch: refetchLlmUsage,
  } = useRetrieveRunLlmUsage(runWithDatasets.run.id, opened);

  useEffect(() => {
    if (!opened) {
      return;
    }

    void refetchRunWithDatasets();
    void refetchLlmUsage();
  }, [opened, refetchLlmUsage, refetchRunWithDatasets]);

  const metadataRunWithDatasets = latestRunWithDatasets ?? runWithDatasets;
  const isRefreshing = isFetchingRunWithDatasets || isFetchingLlmUsage;
  const runVersionLabel = getRunAutomationVersionLabel(metadataRunWithDatasets.run) || 'Version unavailable';
  const headerDetails = [
    getRunVersionStateLabel(metadataRunWithDatasets.run),
    getRunSourceLabel(metadataRunWithDatasets.run),
    runVersionLabel,
  ].join(' · ');

  return (
    <StyledModal
      opened={opened}
      onClose={onClose}
      title={
        <Group justify="space-between" align="flex-start" gap="sm" wrap="nowrap">
          <Stack gap={2}>
            <Text size="sm" fw={600} c="#0F172A">
              Run Metadata - {runWithDatasets.run.id}
            </Text>
            <Group gap="sm">
              <Text size="xs" c="dimmed">
                {headerDetails}
              </Text>
              <StatusIndicator status={metadataRunWithDatasets.run.status} />
            </Group>
          </Stack>
          {isRefreshing ? (
            <Group gap="xs" aria-live="polite" wrap="nowrap">
              <Loader size="xs" />
              <Text size="xs" c="dimmed" fw={500}>
                Updating metadata
              </Text>
            </Group>
          ) : null}
        </Group>
      }
      centered
      size="xl"
      withCloseButton={true}
      scrollType="none"
    >
      <Stack gap="md">
        <RunSummaryCard run={metadataRunWithDatasets.run} />
        <Box>
          <MetadataJsonViewer
            height="calc(90vh - 170px)"
            src={{
              run: metadataRunWithDatasets.run,
              input_datasets: metadataRunWithDatasets.input_datasets,
              static_datasets: metadataRunWithDatasets.static_datasets ?? [],
              linked_datasets: metadataRunWithDatasets.linked_datasets ?? [],
              output_datasets: metadataRunWithDatasets.output_datasets,
              llm_usage: llmUsage || null,
            }}
          />
        </Box>
      </Stack>
    </StyledModal>
  );
};

export default RunMetadataModal;
